import { useState, useEffect, useRef } from "react";
import { store, KEYS } from "./storage";
import { SOLO_PROMPT, SPARRING_PROMPT, IMPORT_PROMPT, ROOTS_PROMPT } from "./prompts";
import { getRandom, detectMultiSentence } from "./sentences";
import { extractJSON, shouldAutoImport } from "./parse";
import type {
  AnalysisResult,
  AnalysisStep,
  FailureMode,
  ImportResult,
  VocabTerm,
  SessionStats,
  HistoryEntry,
  Mode,
  Verdict,
} from "./types";

// API endpoint — Pages Function in production, direct in dev
const API_URL =
  "/api/analyze";

const MAX_TOKENS: Record<Mode, number> = {
  roots: 1500,
  solo: 3000,
  challenge: 3000,
  sparring: 2500,
  import: 4000,
};

const MODES: { id: Mode; label: string; desc: string }[] = [
  { id: "solo", label: "Solo", desc: "Enter any sentence — full analysis" },
  { id: "challenge", label: "Challenge", desc: "Guess the form before the reveal" },
  { id: "sparring", label: "Sparring", desc: "Find the hidden fallacy" },
  { id: "import", label: "Import", desc: "Paste a paragraph — analyze all structures" },
  { id: "roots", label: "Root Word א", desc: "One Hebrew word or transliteration — root analysis, binyan, naming candidates" },
];

const VERDICT_STYLE: Record<Verdict, { color: string; bg: string; border: string; icon: string }> = {
  Clean: {
    color: "var(--color-text-success)",
    bg: "var(--color-background-success)",
    border: "var(--color-border-success)",
    icon: "✓",
  },
  "Tight with caveat": {
    color: "var(--color-text-warning)",
    bg: "var(--color-background-warning)",
    border: "var(--color-border-warning)",
    icon: "~",
  },
  Broken: {
    color: "var(--color-text-danger)",
    bg: "var(--color-background-danger)",
    border: "var(--color-border-danger)",
    icon: "✗",
  },
};


const LOADING_STEPS = [
  "Decomposing surface structure...",
  "Mapping logical operators...",
  "Extracting quantifiers...",
  "Testing modal commitments...",
  "Running inference chain...",
  "Checking validity...",
  "Naming what happened...",
];

const DEFAULT_STATS: SessionStats = { sentences: 0, xp: 0, streak: 0, clean: 0, broken: 0, roots: 0, tight: 0 };

export default function App() {
  const [mode, setMode] = useState<Mode>("solo");
  const [level, setLevel] = useState(3);
  const [sentence, setSentence] = useState("");
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [rootsAnalysis, setRootsAnalysis] = useState<Record<string,unknown>|null>(null);
  const [importAnalysis, setImportAnalysis] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vocab, setVocab] = useState<VocabTerm[]>([]);
  const [stats, setStats] = useState<SessionStats>(DEFAULT_STATS);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const [guess, setGuess] = useState("");
  const [spartingGuess, setSpartingGuess] = useState("");
  const [sparringRevealed, setSparringRevealed] = useState(false);
  const [challengeScored, setChallengeScored] = useState(false);
  const [xpAnim, setXpAnim] = useState<string | null>(null);
  const [tab, setTab] = useState<"game" | "ledger" | "history">("game");
  const [modeAutoChanged, setModeAutoChanged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load persisted state
  useEffect(() => {
    (async () => {
      const [v, s, h] = await Promise.all([
        store.get<VocabTerm[]>(KEYS.VOCAB),
        store.get<SessionStats>(KEYS.STATS),
        store.get<HistoryEntry[]>(KEYS.HISTORY),
      ]);
      if (v) setVocab(v);
      if (s) setStats(s);
      if (h) setHistory(h);
    })();
  }, []);

  const persist = async (v: VocabTerm[], s: SessionStats, h: HistoryEntry[]) => {
    await Promise.all([
      store.set(KEYS.VOCAB, v),
      store.set(KEYS.STATS, s),
      store.set(KEYS.HISTORY, h),
    ]);
  };

  const flash = (pts: number) => {
    setXpAnim(`+${pts} XP`);
    setTimeout(() => setXpAnim(null), 1800);
  };

  const resetState = () => {
    setAnalysis(null);
    setImportAnalysis(null);
    setGuess("");
    setSpartingGuess("");
    setSparringRevealed(false);
    setChallengeScored(false);
    setActiveStep(null);
    setError(null);
    setModeAutoChanged(false);
  };

  const handleSentenceChange = (val: string) => {
    setSentence(val);
    resetState();
    setRootsAnalysis(null);
    // Auto-route multi-sentence input to Import mode
    if (mode !== "import" && shouldAutoImport(val)) {
      setMode("import");
      setModeAutoChanged(true);
    }
  };

  const loadRandom = () => {
    setSentence(getRandom(level));
    resetState();
  };


  // Cycle loading steps while analysis is in flight
  useEffect(() => {
    if (!loading) { setLoadStep(0); return; }
    const t = setInterval(() => setLoadStep(s => (s + 1) % LOADING_STEPS.length), 420);
    return () => clearInterval(t);
  }, [loading]);

  const getSystemPrompt = () => {
    if (mode === "sparring") return SPARRING_PROMPT;
    if (mode === "import") return IMPORT_PROMPT;
    if (mode === "roots") return ROOTS_PROMPT;
    return SOLO_PROMPT;
  };

  const runAnalysis = async () => {
    if (!sentence.trim()) return;
    setLoading(true);
    setError(null);
    resetState();

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: MAX_TOKENS[mode],
          system: getSystemPrompt(),
          messages: [{ role: "user", content: `Analyze: "${sentence}"` }],
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error((errData as { error?: string }).error ?? `HTTP ${res.status}`);
      }

      const data = await res.json() as { content: Array<{ type: string; text?: string }> };
      const raw: string = data.content?.find((b: { type: string }) => b.type === "text")?.text ?? "";

      let xpEarned = 10;

      if (mode === "import") {
        const parsed = extractJSON<ImportResult>(raw);
        if (!parsed) throw new Error("parse_failed");
        setImportAnalysis(parsed);
        xpEarned = (parsed.sentences?.length ?? 1) * 8;

        // Merge new terms
        const newVocab = mergeVocab(vocab, parsed.new_terms ?? []);
        const newStats = updateStats(stats, parsed.overall_verdict, xpEarned);
        const newHistory = addHistory(history, sentence, parsed.overall_verdict, "—", mode);
        setVocab(newVocab);
        setStats(newStats);
        setHistory(newHistory);
        flash(xpEarned);
        await persist(newVocab, newStats, newHistory);
      } else if (mode === "roots") {
        let parsed: Record<string,unknown>;
        try { parsed = JSON.parse(raw) as Record<string,unknown>; }
        catch { const m = raw.match(/{[\s\S]*}/); if (!m) throw new Error("parse_failed"); parsed = JSON.parse(m[0]) as Record<string,unknown>; }
        setRootsAnalysis(parsed);
        const xp = 15;
        const verdict = (parsed.verdict as string) ?? "Clean";
        const newVocab = mergeVocab(vocab, []);
        const newStats = updateStats(stats, verdict as import("./types").Verdict, xp);
        const newHistory = addHistory(history, sentence, verdict as import("./types").Verdict, (parsed.root_consonants as string) ?? "—", mode);
        setVocab(newVocab); setStats(newStats); setHistory(newHistory);
        flash(xp);
        await persist(newVocab, newStats, newHistory);
      } else {
        const parsed = extractJSON<AnalysisResult>(raw);
        if (!parsed) throw new Error("parse_failed");
        setAnalysis(parsed);
        setActiveStep("step1");

        xpEarned =
          parsed.verdict === "Clean" ? 10 : parsed.verdict === "Broken" ? 15 : 12;
        // Bonus for new terms
        xpEarned += (parsed.new_terms?.length ?? 0) * 5;

        const newVocab = mergeVocab(vocab, parsed.new_terms ?? []);
        const newStats = updateStats(stats, parsed.verdict, xpEarned);
        const newHistory = addHistory(history, sentence, parsed.verdict, parsed.formal_name, mode);
        setVocab(newVocab);
        setStats(newStats);
        setHistory(newHistory);
        flash(xpEarned);
        await persist(newVocab, newStats, newHistory);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "unknown";
      if (msg === "parse_failed") {
        setError(
          "Parse failed. If this is a multi-sentence block, switch to Import mode. " +
            "If you entered a single sentence, try simplifying or rephrasing it."
        );
      } else if (msg.includes("Rate limit")) {
        setError("Rate limit reached — wait a moment and try again.");
      } else {
        setError(`Analysis failed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const scoreChallenge = () => {
    if (!analysis || challengeScored) return;
    const g = guess.toLowerCase().trim();
    const fn = (analysis.formal_name ?? "").toLowerCase();
    let pts = 0;
    if (g && fn.split(" ").some((w) => g.includes(w) && w.length > 3)) pts += 15;
    if (g.includes(analysis.verdict.toLowerCase().split(" ")[0])) pts += 10;
    if (pts > 0) {
      const ns = { ...stats, xp: stats.xp + pts };
      setStats(ns);
      persist(vocab, ns, history);
      flash(pts);
    }
    setChallengeScored(true);
  };

  const scoreSparring = () => {
    if (!analysis || sparringRevealed) return;
    const g = spartingGuess.toLowerCase().trim();
    const actual = (analysis.step7?.failures?.[0]?.name ?? "").toLowerCase();
    let pts = 0;
    if (g && actual && actual.split(" ").some((w) => g.includes(w) && w.length > 4)) pts = 25;
    else if (g.length > 3) pts = 5; // partial credit for an attempt
    if (pts > 0) {
      const ns = { ...stats, xp: stats.xp + pts };
      setStats(ns);
      persist(vocab, ns, history);
      flash(pts);
    }
    setSparringRevealed(true);
  };

  // --- Helpers ---
  const mergeVocab = (
    existing: VocabTerm[],
    incoming: Partial<VocabTerm>[]
  ): VocabTerm[] => {
    const next = [...existing];
    for (const t of incoming) {
      if (t.term && !next.find((v) => v.term === t.term)) {
        next.unshift({ term: t.term, definition: t.definition ?? "", in_sentence: t.in_sentence, date: new Date().toISOString() });
      }
    }
    return next;
  };

  const updateStats = (s: SessionStats, verdict: Verdict, xp: number): SessionStats => ({
    sentences: s.sentences + 1,
    xp: s.xp + xp,
    streak: s.streak + 1,
    clean: s.clean + (verdict === "Clean" ? 1 : 0),
    broken: s.broken + (verdict === "Broken" ? 1 : 0),
    tight: s.tight + (verdict === "Tight with caveat" ? 1 : 0),
    roots: s.roots,
  });

  const addHistory = (
    h: HistoryEntry[],
    text: string,
    verdict: Verdict,
    formal_name: string,
    m: Mode
  ): HistoryEntry[] =>
    [
      {
        sentence: text.slice(0, 70) + (text.length > 70 ? "…" : ""),
        verdict,
        formal_name,
        mode: m,
        ts: new Date().toLocaleTimeString(),
      },
      ...h,
    ].slice(0, 25);


  // ── Export analysis to clipboard ──────────────────────────────────────────
  const copyAnalysis = () => {
    if (!analysis) return;
    const lines = [
      `# Analysis: ${sentence.slice(0, 60)}${sentence.length > 60 ? "…" : ""}`,
      `**Verdict:** ${analysis.verdict}`,
      analysis.formal_name ? `**Form:** ${analysis.formal_name}` : "",
      analysis.verdict_note ? `**Note:** ${analysis.verdict_note}` : "",
      "",
      analysis.step1?.content ? `**Decomposition:** ${analysis.step1.content}` : "",
      analysis.step2?.notation ? `**Notation:** ${analysis.step2.notation}` : "",
      "",
      analysis.presuppositions?.length
        ? "**Presuppositions:**\n" + analysis.presuppositions.map((p: string) => "- " + p).join("\n")
        : "",
      "",
      analysis.new_terms?.length
        ? "**Terms acquired:**\n" + analysis.new_terms.map((t: { term: string; definition: string }) => "- **" + t.term + "**: " + t.definition).join("\n")
        : "",
      "",
      `---`,
      `Analyzed with Catch The Pattern™ — logic.naci.tech/app`,
    ].filter(Boolean).join("\n");
    const el = document.createElement("textarea");

    el.value = lines;

    el.style.position = "fixed";

    el.style.opacity = "0";

    document.body.appendChild(el);

    el.select();

    document.execCommand("copy");

    document.body.removeChild(el);

    navigator.clipboard?.writeText(lines).catch(() => {});

    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  // --- Derived ---
  const steps = analysis
    ? ([
        { key: "step1", label: "S1 Decompose" },
        { key: "step2", label: "S2 Operators" },
        { key: "step3", label: "S3 Quantifiers" },
        !analysis.step4?.skip && { key: "step4", label: "S4 Modal" },
        !analysis.step5?.skip && { key: "step5", label: "S5 Inference" },
        !analysis.step6?.skip && { key: "step6", label: "S6 Validity" },
        { key: "step7", label: "S7 Failures" },
      ].filter(Boolean) as { key: string; label: string }[])
    : [];

  const vs = analysis
    ? VERDICT_STYLE[analysis.verdict] ?? VERDICT_STYLE["Clean"]
    : null;

  const activeStepData = activeStep
    ? (analysis as unknown as Record<string, AnalysisStep>)?.[activeStep]
    : undefined;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "var(--font-sans)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
      <style>{`
        .lrg-tab{cursor:pointer;padding:5px 12px;border-radius:20px;font-size:12px;border:0.5px solid var(--color-border-tertiary);background:transparent;color:var(--color-text-secondary);transition:all 0.15s}
        .lrg-tab:hover{border-color:var(--color-border-secondary);color:var(--color-text-primary)}
        .lrg-tab.on{background:var(--color-background-info);border-color:var(--color-border-info);color:var(--color-text-info)}
        .lrg-step{cursor:pointer;padding:4px 10px;border-radius:4px;font-size:11px;font-family:var(--font-mono);border:0.5px solid var(--color-border-tertiary);background:transparent;color:var(--color-text-tertiary);transition:all 0.15s;white-space:nowrap}
        .lrg-step:hover{border-color:var(--color-border-primary);color:var(--color-text-secondary)}
        .lrg-step.on{background:var(--color-background-secondary);border-color:var(--color-border-secondary);color:var(--color-text-primary)}
        .lrg-lvl{cursor:pointer;width:26px;height:26px;border-radius:50%;font-size:11px;font-family:var(--font-mono);border:0.5px solid var(--color-border-tertiary);background:transparent;color:var(--color-text-tertiary);display:flex;align-items:center;justify-content:center;transition:all 0.15s}
        .lrg-lvl:hover{border-color:var(--color-border-secondary)}
        .lrg-lvl.on{background:var(--color-background-info);border-color:var(--color-border-info);color:var(--color-text-info)}
        .lrg-btn{cursor:pointer;padding:8px 18px;border-radius:var(--border-radius-md);font-size:13px;border:0.5px solid var(--color-border-secondary);background:transparent;color:var(--color-text-primary);transition:all 0.15s}
        .lrg-btn:hover{background:var(--color-background-secondary)}
        .lrg-btn:disabled{opacity:0.35;cursor:not-allowed}
        .lrg-primary{background:var(--color-background-info)!important;border-color:var(--color-border-info)!important;color:var(--color-text-info)!important}
        .lrg-slide{animation:lrg-in 0.25s ease}
        @keyframes lrg-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        .xp-pop{position:fixed;top:60px;right:24px;font-size:14px;font-weight:500;color:var(--color-text-success);animation:xp-anim 1.8s ease forwards;pointer-events:none;font-family:var(--font-mono);z-index:99}
        @keyframes xp-anim{0%{opacity:0;transform:translateY(0)}15%{opacity:1;transform:translateY(-8px)}80%{opacity:1}100%{opacity:0;transform:translateY(-24px)}}
        .notation{font-family:var(--font-mono);font-size:13px;background:var(--color-background-secondary);border-left:2px solid var(--color-border-info);padding:8px 12px;margin-top:8px;color:var(--color-text-info)}
        .presup{display:inline-block;font-size:11px;background:var(--color-background-warning);border:0.5px solid var(--color-border-warning);color:var(--color-text-warning);padding:2px 8px;border-radius:var(--border-radius-md);margin-right:4px;margin-top:4px}
        .vpill{display:inline-block;font-size:11px;background:var(--color-background-info);border:0.5px solid var(--color-border-info);color:var(--color-text-info);padding:2px 8px;border-radius:var(--border-radius-md);font-family:var(--font-mono)}
        .spin{display:inline-block;width:14px;height:14px;border:2px solid var(--color-border-tertiary);border-top-color:var(--color-text-info);border-radius:50%;animation:spin 0.7s linear infinite}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {xpAnim && <div className="xp-pop">{xpAnim}</div>}

      {/* Header */}
      <div style={{ borderBottom: "0.5px solid var(--color-border-tertiary)", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 400 }}>Catch The Pattern™</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-tertiary)", letterSpacing: "0.08em" }}>EMET</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {(["game", "ledger", "history"] as const).map((t) => (
            <button key={t} className={`lrg-tab ${tab === t ? "on" : ""}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {[
            ["XP", stats.xp, "var(--color-text-info)"],
            ["streak", stats.streak, "var(--color-text-success)"],
            ["terms", vocab.length, "var(--color-text-secondary)"],
          ].map(([l, v, c]) => (
            <div key={String(l)} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 500, color: String(c) }}>{v}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* GAME TAB */}
      {tab === "game" && (
        <div style={{ flex: 1, padding: "20px", display: "flex", flexDirection: "column", gap: 14, maxWidth: 780, width: "100%" }}>

          {/* Mode + Level */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {MODES.map((m) => (
                <button
                  key={m.id}
                  className={`lrg-tab ${mode === m.id ? "on" : ""}`}
                  title={m.desc}
                  onClick={() => { setMode(m.id); resetState(); }}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {mode !== "import" && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--color-text-tertiary)" }}>L</span>
                {([1, 2, 3, 4, 5, 6] as const).map((l) => (
                  <button key={l} className={`lrg-lvl ${level === l ? "on" : ""}`} onClick={() => setLevel(l)}>{l}</button>
                ))}
              </div>
            )}
          </div>

          {/* Mode hint */}
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", fontStyle: "italic" }}>
            {MODES.find((m) => m.id === mode)?.desc}
            {modeAutoChanged && " (auto-routed — multi-sentence input detected)"}
          </div>

          {/* Input */}
          <div>
            <textarea
              ref={textareaRef}
              rows={mode === "import" ? 5 : 3}
              value={sentence}
              onChange={(e) => handleSentenceChange(e.target.value)}
              placeholder={
                mode === "roots"
                  ? "One word — Hebrew or transliteration (emet, shalom, katav, naci…)"
                  : mode === "import"
                  ? "Paste a paragraph or 3–10 sentences — full argument analysis"
                  : mode === "challenge"
                  ? "Enter a sentence (15–40 words ideal) — guess the form first"
                  : mode === "sparring"
                  ? "Enter a sentence with a hidden fallacy — find it before the reveal"
                  : "Enter a sentence or short claim — 2–3 sentences ok"
              }
              style={{
                width: "100%", padding: "12px 14px",
                fontSize: mode === "import" ? 13 : 15,
                fontFamily: mode === "import" ? "var(--font-sans)" : "var(--font-serif)",
                borderRadius: "var(--border-radius-md)",
                border: "0.5px solid var(--color-border-secondary)",
                background: "var(--color-background-secondary)",
                color: "var(--color-text-primary)",
                outline: "none", resize: "none", lineHeight: 1.65,
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              {mode !== "import" && (
                <button className="lrg-btn" onClick={loadRandom} style={{ fontSize: 12 }}>↻ Random L{level}</button>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {mode === "challenge" && !challengeScored && (
                  <input
                    value={guess}
                    onChange={(e) => setGuess(e.target.value)}
                    placeholder="Your identification (optional)…"
                    style={{ padding: "7px 12px", fontSize: 12, fontFamily: "var(--font-mono)", borderRadius: "var(--border-radius-md)", border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-secondary)", color: "var(--color-text-primary)", outline: "none", width: 220 }}
                  />
                )}
                <button
                  className="lrg-btn lrg-primary"
                  onClick={runAnalysis}
                  disabled={loading || !sentence.trim()}
                >
                  {loading ? <><span className="spin" style={{ marginRight: 6 }} />Analyzing…</> : "Analyze →"}
                </button>
              </div>
            </div>
          </div>

          {/* Error */}

              {/* Loading step animator */}
              {loading && (
                <div style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--color-text-tertiary)",
                  padding: "16px 0 0",
                  letterSpacing: ".05em",
                  minHeight: 22,
                }}>
                  {LOADING_STEPS[loadStep]}
                </div>
              )}

          {error && (
            <div style={{ padding: "10px 14px", borderRadius: "var(--border-radius-md)", background: "var(--color-background-danger)", border: "0.5px solid var(--color-border-danger)", color: "var(--color-text-danger)", fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Analysis output */}
          {/* Roots א result card */}
          {rootsAnalysis && mode === 'roots' && (
            <div style={{ background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-secondary)', borderRadius: 'var(--border-radius-lg)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: rootsAnalysis.verdict === 'Clean' ? 'var(--color-verdict-clean)' : rootsAnalysis.verdict === 'Broken' ? 'var(--color-verdict-broken)' : 'var(--color-verdict-tight)' }}>{rootsAnalysis.verdict as string}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)' }}>{rootsAnalysis.root_consonants as string}</span>
                {!!rootsAnalysis.root_hebrew && <span style={{ fontFamily: 'serif', fontSize: 18, direction: 'rtl', color: 'var(--color-text-secondary)' }}>{rootsAnalysis.root_hebrew as string}</span>}
              </div>
              <div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-text-tertiary)', letterSpacing: '.1em', marginBottom: 4 }}>ROOT TERRITORY</div><div style={{ fontSize: 14, color: 'var(--color-text-primary)', lineHeight: 1.6 }}>{rootsAnalysis.root_meaning as string}</div></div>
              {(rootsAnalysis.binyan as {algebra?:string,name?:string})?.algebra && (<div style={{ borderLeft: '2px solid var(--color-border-secondary)', paddingLeft: 12 }}><div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-text-tertiary)', letterSpacing: '.1em', marginBottom: 4 }}>BINYAN — {(rootsAnalysis.binyan as {name:string}).name}</div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>{(rootsAnalysis.binyan as {algebra:string}).algebra}</div></div>)}
              {(rootsAnalysis.cross_domain as {aetherion_node?:string,logical_parallel?:string}) && (<div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-text-tertiary)', letterSpacing: '.1em', marginBottom: 6 }}>CROSS-DOMAIN</div><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', minWidth: 0 }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--color-background-tertiary)', padding: '3px 8px', borderRadius: 'var(--border-radius-sm)', color: 'var(--color-text-secondary)', flexShrink: 0, whiteSpace: 'nowrap' }}>⬡ {(rootsAnalysis.cross_domain as {aetherion_node:string}).aetherion_node}</span><span style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.5, overflowWrap: 'anywhere', minWidth: 0, flex: 1, wordBreak: 'break-word' }}>{(rootsAnalysis.cross_domain as {logical_parallel:string}).logical_parallel}</span></div></div>)}
              {((rootsAnalysis.cross_domain as {naming_candidates?:Array<{name:string,form:string,meaning:string,uspto_viability:string,domain_note:string}>})?.naming_candidates?.length ?? 0) > 0 && (<div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-text-tertiary)', letterSpacing: '.1em', marginBottom: 6 }}>NAMING CANDIDATES</div><div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{((rootsAnalysis.cross_domain as {naming_candidates:Array<{name:string,form:string,meaning:string,uspto_viability:string,domain_note:string}>}).naming_candidates).map((c,i)=>(<div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 600 }}>{c.name}</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)' }}>{c.form}</span><span style={{ fontSize: 12, color: 'var(--color-text-secondary)', flex: 1 }}>{c.meaning}</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: c.uspto_viability === 'high' ? 'var(--color-verdict-clean)' : c.uspto_viability === 'low' ? 'var(--color-verdict-broken)' : 'var(--color-verdict-tight)' }}>USPTO:{c.uspto_viability}</span></div>))}</div></div>)}
              {((rootsAnalysis.semantic_family as Array<{word:string,transliteration:string,meaning:string}>)?.length ?? 0) > 0 && (<div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-text-tertiary)', letterSpacing: '.1em', marginBottom: 6 }}>SEMANTIC FAMILY</div><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{(rootsAnalysis.semantic_family as Array<{word:string,transliteration:string,meaning:string}>).map((w,i)=>(<span key={i} title={w.meaning} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, background: 'var(--color-background-tertiary)', padding: '3px 8px', borderRadius: 'var(--border-radius-sm)', color: 'var(--color-text-secondary)', cursor: 'default' }}>{w.word} ({w.transliteration})</span>))}</div></div>)}
              {!!rootsAnalysis.verdict_note && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)', borderTop: '1px solid var(--color-border-tertiary)', paddingTop: 10, lineHeight: 1.6 }}>{rootsAnalysis.verdict_note as string}</div>}
            </div>
          )}
          {analysis && vs && (
            <div className="lrg-slide">
              {/* Verdict */}
              <div style={{ background: vs.bg, border: `0.5px solid ${vs.border}`, borderRadius: "var(--border-radius-lg)", padding: "14px 18px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20, color: vs.color }}>{vs.icon}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 500, color: vs.color, fontFamily: "var(--font-serif)" }}>{analysis.verdict}</div>
                    <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>{analysis.verdict_note}</div>
                  </div>
                </div>
                {analysis.formal_name && (
                  <span className="vpill" style={{ padding: "4px 10px", fontSize: 12 }}>{analysis.formal_name}</span>
                )}
                <button
                  onClick={copyAnalysis}
                  title="Copy analysis to clipboard"
                  style={{
                    background: "transparent",
                    border: "0.5px solid var(--color-border-tertiary)",
                    color: "var(--color-text-tertiary)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    padding: "3px 8px",
                    cursor: "pointer",
                    borderRadius: "var(--border-radius-md)",
                    letterSpacing: ".04em",
                    marginLeft: "auto",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = "var(--color-text-secondary)")}
                  onMouseLeave={e => (e.currentTarget.style.color = "var(--color-text-tertiary)")}
                >
                  {copied ? "Copied ✓" : "Export →"}
                </button>
              </div>

              {/* Challenge compare */}
              {mode === "challenge" && guess && (
                <div style={{ display: "flex", gap: 20, padding: "10px 14px", background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)", marginBottom: 10, border: "0.5px solid var(--color-border-tertiary)", fontSize: 13, alignItems: "center" }}>
                  <div><div style={{ color: "var(--color-text-tertiary)", fontSize: 10, marginBottom: 2 }}>YOUR ID</div><span style={{ fontFamily: "var(--font-mono)" }}>{guess}</span></div>
                  <div style={{ width: "0.5px", background: "var(--color-border-tertiary)", alignSelf: "stretch" }} />
                  <div><div style={{ color: "var(--color-text-tertiary)", fontSize: 10, marginBottom: 2 }}>FORMAL</div><span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-info)" }}>{analysis.formal_name}</span></div>
                  {!challengeScored && (
                    <button className="lrg-btn lrg-primary" style={{ marginLeft: "auto" }} onClick={scoreChallenge}>Score</button>
                  )}
                </div>
              )}

              {/* Sparring hunt */}
              {mode === "sparring" && !sparringRevealed && (
                <div style={{ padding: "12px 14px", background: "var(--color-background-warning)", border: "0.5px solid var(--color-border-warning)", borderRadius: "var(--border-radius-md)", marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--color-text-warning)", marginBottom: 8 }}>One fallacy is present. Name it, then reveal.</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      value={spartingGuess}
                      onChange={(e) => setSpartingGuess(e.target.value)}
                      placeholder="Name the fallacy…"
                      style={{ flex: 1, padding: "7px 12px", fontSize: 12, fontFamily: "var(--font-mono)", borderRadius: "var(--border-radius-md)", border: "0.5px solid var(--color-border-warning)", background: "transparent", color: "var(--color-text-primary)", outline: "none" }}
                    />
                    <button className="lrg-btn" onClick={scoreSparring}>Reveal →</button>
                  </div>
                </div>
              )}
              {mode === "sparring" && sparringRevealed && analysis.step7?.failures?.[0] && (
                <div style={{ padding: "10px 14px", background: "var(--color-background-danger)", border: "0.5px solid var(--color-border-danger)", borderRadius: "var(--border-radius-md)", marginBottom: 10 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 500, color: "var(--color-text-danger)" }}>{analysis.step7.failures[0].name}</span>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>{analysis.step7.failures[0].explanation}</div>
                </div>
              )}

              {/* Step tabs */}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                {steps.map((s) => (
                  <button key={s.key} className={`lrg-step ${activeStep === s.key ? "on" : ""}`} onClick={() => setActiveStep(activeStep === s.key ? null : s.key)}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Step content */}
              {activeStep && activeStepData && (
                <div style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-md)", padding: "14px 16px", marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--color-text-tertiary)", letterSpacing: "0.06em", marginBottom: 8 }}>{activeStepData.name?.toUpperCase()}</div>
                  <div style={{ fontSize: 14, color: "var(--color-text-primary)", lineHeight: 1.7 }}>{activeStepData.content}</div>
                  {activeStepData.notation && <div className="notation">{activeStepData.notation}</div>}
                  {activeStepData.failures && activeStepData.failures.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {activeStepData.failures.map((f: FailureMode, i: number) => (
                        <div key={i} style={{ background: "var(--color-background-danger)", border: "0.5px solid var(--color-border-danger)", borderRadius: "var(--border-radius-md)", padding: "8px 12px", marginBottom: 6 }}>
                          <span style={{ color: "var(--color-text-danger)", fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 500 }}>{f.name}</span>
                          <div style={{ color: "var(--color-text-secondary)", fontSize: 12, marginTop: 4 }}>{f.explanation}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Presuppositions */}
              {analysis.presuppositions && analysis.presuppositions.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--color-text-tertiary)", marginBottom: 6 }}>PRESUPPOSITIONS EMBEDDED</div>
                  {analysis.presuppositions.map((p, i) => <span key={i} className="presup">{p}</span>)}
                </div>
              )}

              {/* New terms */}
              {analysis.new_terms && analysis.new_terms.length > 0 && (
                <div style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-md)", padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--color-text-tertiary)", letterSpacing: "0.06em", marginBottom: 10 }}>TERMS ACQUIRED</div>
                  {analysis.new_terms.map((t, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <span className="vpill">{t.term}</span>
                      <span style={{ fontSize: 13, color: "var(--color-text-secondary)", marginLeft: 8 }}>{t.definition}</span>
                      {t.in_sentence && <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 3, paddingLeft: 8, borderLeft: "2px solid var(--color-border-tertiary)" }}>{t.in_sentence}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Import output */}
          {importAnalysis && (
            <div className="lrg-slide">
              <div style={{ marginBottom: 12, fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>{importAnalysis.argument_structure}</div>
              {importAnalysis.sentences?.map((s, i) => {
                const v = VERDICT_STYLE[s.verdict] ?? VERDICT_STYLE.Clean;
                return (
                  <div key={i} style={{ padding: "10px 12px", borderRadius: "var(--border-radius-md)", border: "0.5px solid var(--color-border-tertiary)", marginBottom: 8, background: "var(--color-background-secondary)" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-tertiary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-md)", padding: "2px 6px", whiteSpace: "nowrap" }}>{s.role}</span>
                      <span style={{ fontSize: 13, color: "var(--color-text-primary)", lineHeight: 1.5 }}>{s.text}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ color: v.color, fontSize: 12 }}>{v.icon}</span>
                      <span className="vpill" style={{ fontSize: 11 }}>{s.formal_name}</span>
                      <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>{s.key_issue}</span>
                    </div>
                  </div>
                );
              })}
              {importAnalysis.top_failure && (
                <div style={{ padding: "10px 14px", background: "var(--color-background-danger)", border: "0.5px solid var(--color-border-danger)", borderRadius: "var(--border-radius-md)", marginTop: 8, fontSize: 13, color: "var(--color-text-danger)" }}>
                  Top issue: {importAnalysis.top_failure}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* LEDGER TAB */}
      {tab === "ledger" && (
        <div style={{ flex: 1, padding: "20px", maxWidth: 780 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 500 }}>Vocabulary ledger — {vocab.length} terms</span>
            <div style={{ display: "flex", gap: 8 }}>
              {([["Clean", stats.clean, "var(--color-text-success)"], ["Tight", stats.tight, "var(--color-text-warning)"], ["Broken", stats.broken, "var(--color-text-danger)"]] as const).map(([l, v, c]) => (
                <div key={l} style={{ textAlign: "center", padding: "6px 12px", background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)" }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, color: c }}>{v}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          {vocab.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 14 }}>Analyze sentences to build your ledger.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
              {vocab.map((v, i) => (
                <div key={i} style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-lg)", padding: "12px 14px" }}>
                  <span className="vpill" style={{ display: "inline-block", marginBottom: 6 }}>{v.term}</span>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>{v.definition}</div>
                  {v.in_sentence && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 6, borderTop: "0.5px solid var(--color-border-tertiary)", paddingTop: 6 }}>{v.in_sentence}</div>}
                </div>
              ))}
            </div>
          )}
          {vocab.length > 0 && (
            <button className="lrg-btn" style={{ marginTop: 20, fontSize: 11 }} onClick={async () => { await store.remove(KEYS.VOCAB); setVocab([]); }}>Clear ledger</button>
          )}
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <div style={{ flex: 1, padding: "20px", maxWidth: 780 }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Session history — {history.length} analyzed</div>
          {history.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 14 }}>No history yet.</div>
          ) : history.map((h, i) => {
            const v = VERDICT_STYLE[h.verdict] ?? VERDICT_STYLE.Clean;
            return (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                <span style={{ color: v.color, fontSize: 14, minWidth: 14 }}>{v.icon}</span>
                <div style={{ flex: 1, fontSize: 13 }}>{h.sentence}</div>
                <span className="vpill" style={{ fontSize: 10, whiteSpace: "nowrap" }}>{h.formal_name}</span>
                <span style={{ fontSize: 10, color: "var(--color-text-tertiary)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{h.ts}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
